import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { CreditCard, Lock, Loader2, AlertCircle, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { processPayment } from "@/modules/eventRegistration/features/EventRegistration/api/processPayment/processPaymentApi";
import { validateCheckoutAvailability } from "@/modules/eventRegistration/features/EventRegistration/api/validateCheckoutAvailability/validateCheckoutAvailabilityApi";
import { ValidateCheckoutAvailabilityResponseData } from "@/modules/eventRegistration/features/EventRegistration/api/validateCheckoutAvailability/types";
import { calculateCheckoutPrice } from "@/modules/eventRegistration/features/EventRegistration/utils/checkoutPriceCalculator";
import errorCodeToMessage from "@/modules/eventRegistration/features/EventRegistration/utils/errorMappings";

interface SecureData {
  authData: {
    clientKey: string;
    apiLoginID: string;
  };
  cardData: {
    cardNumber: string;
    month: string;
    year: string;
    cardCode: string;
    fullName: string;
  };
}

declare global {
  interface Window {
    Accept?: {
      dispatchData: (
        secureData: SecureData,
        callback: (response: AcceptResponse) => void,
      ) => void;
    };
  }
}

interface AcceptResponse {
  messages: {
    resultCode: string;
    message: Array<{
      code: string;
      text: string;
    }>;
  };
  opaqueData: {
    dataDescriptor: string;
    dataValue: string;
  };
}

type AdminDiscountType = "percentage" | "fixed_amount" | null;

interface PaymentFormProps {
  total?: number;
  isProcessing?: boolean;
  setIsProcessing?: (value: boolean) => void;
  handlePaymentSuccess?: () => void;
  voucherQuantity?: number;
  discountCode?: string | null;
  discountAmount?: number;
  adminDiscountType?: AdminDiscountType;
  adminDiscountValue?: number;
  adminDiscountReason?: string;
  eventCheckoutSessionUuid?: string;
  onAvailabilityChanged?: (
    validationData: ValidateCheckoutAvailabilityResponseData,
  ) => void;
  eventPrice?: number;
  disabled?: boolean;
}

interface ApiError {
  response?: {
    data?: {
      errorCode?: string;
      message?: string;
    };
  };
}

// UPDATED: formatCardNumber function to handle American Express formatting
function formatCardNumber(value: string): string {
  let numericValue = value.replace(/\D/g, "");

  // Check if it's an American Express card (starts with 34 or 37)
  const isAmex = numericValue.startsWith("34") || numericValue.startsWith("37");

  if (isAmex) {
    // Amex has 15 digits and uses 4-6-5 format
    numericValue = numericValue.slice(0, 15);

    if (numericValue.length <= 4) {
      return numericValue;
    } else if (numericValue.length <= 10) {
      return `${numericValue.slice(0, 4)} ${numericValue.slice(4)}`;
    } else {
      return `${numericValue.slice(0, 4)} ${numericValue.slice(4, 10)} ${numericValue.slice(10)}`;
    }
  } else {
    // Standard cards have 16 digits and use 4-4-4-4 format
    numericValue = numericValue.slice(0, 16);
    const chunks = numericValue.match(/.{1,4}/g);
    return chunks ? chunks.join(" ") : numericValue;
  }
}

function generateUniqueInvoiceNumber(): string {
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, "0");
  return `INV${timestamp}${random}`;
}

function PaymentForm({
  total = 0,
  isProcessing = false,
  setIsProcessing = () => {},
  handlePaymentSuccess = () => {},
  voucherQuantity = 0,
  discountCode = null,
  discountAmount = 0,
  adminDiscountType = null,
  adminDiscountValue = 0,
  adminDiscountReason = "",
  eventCheckoutSessionUuid,
  onAvailabilityChanged,
  eventPrice = 0,
  disabled = false,
}: PaymentFormProps) {
  const [cardholderName, setCardholderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryMonth, setExpiryMonth] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [cardCode, setCardCode] = useState("");
  const [saveOnFile] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState(
    generateUniqueInvoiceNumber(),
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [showChangesDialog, setShowChangesDialog] = useState(false);
  const [availabilityChanges, setAvailabilityChanges] = useState<string[]>([]);
  const [availabilityMessage, setAvailabilityMessage] = useState<string | null>(
    null,
  );
  const [pendingSecureData, setPendingSecureData] = useState<SecureData | null>(
    null,
  );
  const [validationData, setValidationData] =
    useState<ValidateCheckoutAvailabilityResponseData | null>(null);
  const [updatedTotal, setUpdatedTotal] = useState<number | null>(null);

  const apiLoginID = process.env.REACT_APP_AUTHNET_API_LOGIN_ID || "";
  const clientKey = process.env.REACT_APP_AUTHNET_CLIENT_KEY || "";

  const regenerateInvoiceNumber = () => {
    setInvoiceNumber(generateUniqueInvoiceNumber());
  };

  const handleAcceptResponse = async (response: AcceptResponse) => {
    if (response.messages.resultCode === "Error") {
      if (
        Array.isArray(response.messages.message) &&
        response.messages.message.length
      ) {
        const userMessages = response.messages.message.map((msg) => {
          const friendlyMessage = errorCodeToMessage[msg.code] || msg.text;
          return friendlyMessage;
        });
        setErrorMessage(userMessages.join("\n"));
      }
      setIsProcessing(false);
      return;
    }
    setErrorMessage("");
    const { dataDescriptor, dataValue } = response.opaqueData;
    try {
      await processPayment({
        dataDescriptor,
        dataValue,
        amount: updatedTotal !== null ? updatedTotal : total,
        shouldCreatePaymentProfile: saveOnFile,
        invoiceNumber,
        voucherQuantity,
        discountCode: discountCode || undefined,
        discountAmount,
        adminDiscountType: adminDiscountType || undefined,
        adminDiscountValue,
        adminDiscountReason,
        eventCheckoutSessionUuid,
      });
      setIsProcessing(false);
      handlePaymentSuccess();
    } catch (error: unknown) {
      setIsProcessing(false);
      const apiError = error as ApiError;
      const errorMsg = apiError.response?.data?.message;
      setErrorMessage(
        errorMsg ||
          "An error occurred while processing your payment. Please try again.",
      );
      regenerateInvoiceNumber();
    }
  };

  const proceedWithPayment = () => {
    setShowChangesDialog(false);
    if (
      pendingSecureData &&
      window.Accept &&
      typeof window.Accept.dispatchData === "function"
    ) {
      window.Accept.dispatchData(pendingSecureData, handleAcceptResponse);
    } else {
      setIsProcessing(false);
      setErrorMessage(
        "Payment functionality is currently unavailable. Please contact support.",
      );
    }
  };

  const handleCancelAfterChanges = () => {
    setShowChangesDialog(false);
    setIsProcessing(false);
    setValidationData(null);
    setUpdatedTotal(null);
  };

  const handleManageWaitlist = () => {
    setShowChangesDialog(false);
    setIsProcessing(false);
  };

  const handlePayment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsProcessing(true);
    setIsValidating(true);
    setErrorMessage("");
    regenerateInvoiceNumber();

    // First validate availability
    if (eventCheckoutSessionUuid) {
      try {
        const validationResult = await validateCheckoutAvailability(
          eventCheckoutSessionUuid,
        );
        setIsValidating(false);
        setValidationData(validationResult.data);

        if (validationResult.data.hasChanges) {
          setAvailabilityChanges(validationResult.data.changes);
          setAvailabilityMessage(validationResult.data.message);

          // Calculate updated pricing
          const pricingAfterChanges = calculateCheckoutPrice(
            validationResult.data.attendees.filter((a) => a.isSelected),
            eventPrice,
            voucherQuantity,
            discountAmount,
            adminDiscountValue *
              (adminDiscountType === "percentage" ? eventPrice / 100 : 1),
          );
          setUpdatedTotal(pricingAfterChanges.total);

          // Notify parent to update attendee status
          if (onAvailabilityChanged) {
            onAvailabilityChanged(validationResult.data);
          }

          const secureData: SecureData = {
            authData: {
              clientKey,
              apiLoginID,
            },
            cardData: {
              cardNumber: cardNumber.replace(/\s+/g, ""),
              month: expiryMonth,
              year: expiryYear,
              cardCode,
              fullName: cardholderName,
            },
          };
          setPendingSecureData(secureData);
          setShowChangesDialog(true);
          return;
        }
      } catch (error) {
        setIsValidating(false);
        setIsProcessing(false);
        setErrorMessage("Unable to validate availability. Please try again.");
        return;
      }
    }

    // If no changes or no UUID, proceed directly
    const secureData: SecureData = {
      authData: {
        clientKey,
        apiLoginID,
      },
      cardData: {
        cardNumber: cardNumber.replace(/\s+/g, ""),
        month: expiryMonth,
        year: expiryYear,
        cardCode,
        fullName: cardholderName,
      },
    };

    if (window.Accept && typeof window.Accept.dispatchData === "function") {
      window.Accept.dispatchData(secureData, handleAcceptResponse);
    } else {
      setIsProcessing(false);
      setErrorMessage(
        "Payment functionality is currently unavailable. Please contact support.",
      );
    }
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(formatCardNumber(e.target.value));
  };

  const handleExpiryMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    value = value.slice(0, 2);
    setExpiryMonth(value);
  };

  const handleExpiryYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    value = value.slice(0, 2);
    setExpiryYear(value);
  };

  const handleCardCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    value = value.slice(0, 4);
    setCardCode(value);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center">
            <CreditCard className="h-4 w-4 mr-2 text-primary" />
            Payment Information
          </CardTitle>
          <CardDescription>
            Enter your payment details to complete registration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded p-4">
            <div className="mb-4">
              <h2 className="text-lg font-semibold flex items-center">
                <CreditCard className="h-5 w-5 mr-2 text-primary" />
                Payment Information
              </h2>
            </div>
            {errorMessage && (
              <div className="mb-4 p-2 bg-red-50 border border-red-300 text-red-600 rounded">
                {errorMessage}
              </div>
            )}
            <form className="space-y-4" onSubmit={handlePayment}>
              <div className="space-y-2">
                <label
                  className="text-sm font-medium block"
                  htmlFor="cardholderName"
                >
                  Cardholder Name
                </label>
                <input
                  className="w-full border p-2 rounded"
                  disabled={isProcessing}
                  id="cardholderName"
                  onChange={(e) => setCardholderName(e.target.value)}
                  placeholder="John Doe"
                  required
                  type="text"
                  value={cardholderName}
                />
              </div>
              <div className="space-y-2">
                <label
                  className="text-sm font-medium block"
                  htmlFor="cardNumber"
                >
                  Card Number
                </label>
                <input
                  className="w-full border p-2 rounded"
                  disabled={isProcessing}
                  id="cardNumber"
                  onChange={handleCardNumberChange}
                  placeholder="4111 1111 1111 1111"
                  required
                  type="text"
                  value={cardNumber}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label
                    className="text-sm font-medium block"
                    htmlFor="expiryMonth"
                  >
                    Month
                  </label>
                  <input
                    className="w-full border p-2 rounded"
                    disabled={isProcessing}
                    id="expiryMonth"
                    onChange={handleExpiryMonthChange}
                    placeholder="MM"
                    required
                    type="text"
                    value={expiryMonth}
                  />
                </div>
                <div className="space-y-2">
                  <label
                    className="text-sm font-medium block"
                    htmlFor="expiryYear"
                  >
                    Year
                  </label>
                  <input
                    className="w-full border p-2 rounded"
                    disabled={isProcessing}
                    id="expiryYear"
                    onChange={handleExpiryYearChange}
                    placeholder="YY"
                    required
                    type="text"
                    value={expiryYear}
                  />
                </div>
                <div className="space-y-2">
                  <label
                    className="text-sm font-medium block"
                    htmlFor="cardCode"
                  >
                    CVV
                  </label>
                  <input
                    className="w-full border p-2 rounded"
                    disabled={isProcessing}
                    id="cardCode"
                    onChange={handleCardCodeChange}
                    placeholder="123"
                    required
                    type="text"
                    value={cardCode}
                  />
                </div>
              </div>
              <Button className="w-full" disabled={isProcessing || disabled} type="submit">
                {isProcessing ? (
                  <>
                    {isValidating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Validating availability...
                      </>
                    ) : (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Complete Registration (${total.toFixed(2)})
                  </>
                )}
              </Button>
              <div className="text-xs text-center text-gray-500 mt-2">
                <div className="flex items-center justify-center mb-1">
                  <Lock className="h-3 w-3 mr-1" />
                  <span>Secure Payment</span>
                </div>
                <p>
                  Your payment information is encrypted and processed securely.
                </p>
              </div>
            </form>
          </div>
        </CardContent>
      </Card>

      <Dialog onOpenChange={setShowChangesDialog} open={showChangesDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Seat Availability Has Changed
            </DialogTitle>
            <DialogDescription className="pt-4">
              {availabilityMessage && (
                <Alert className="mb-4">
                  <AlertDescription>{availabilityMessage}</AlertDescription>
                </Alert>
              )}
              {validationData && (
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-3">
                    <h4 className="font-medium flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      Attendee Changes
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Previously</p>
                        <p className="font-medium">
                          {validationData.previousSeatedCount} paid seats
                        </p>
                        {validationData.previousWaitlistCount > 0 && (
                          <p className="text-amber-600">
                            {validationData.previousWaitlistCount} waitlisted
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-muted-foreground">Now</p>
                        <p className="font-medium">
                          {validationData.newSeatedCount} paid seats
                        </p>
                        {validationData.newWaitlistCount > 0 && (
                          <p className="text-amber-600">
                            {validationData.newWaitlistCount} waitlisted
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {updatedTotal !== null && updatedTotal !== total && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-2">
                      <h4 className="font-medium">Price Update</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Previous total:
                          </span>
                          <span className="line-through">
                            ${total.toFixed(2)}
                          </span>
                        </div>
                        <Separator className="my-1" />
                        <div className="flex justify-between font-medium">
                          <span>New total:</span>
                          <span className="text-lg">
                            ${updatedTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {availabilityChanges.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-medium text-sm">What happened:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                        {availabilityChanges.map((change, index) => (
                          <li key={index}>{change}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button onClick={handleCancelAfterChanges} variant="outline">
              Cancel Payment
            </Button>
            <Button onClick={proceedWithPayment}>
              Continue with $
              {updatedTotal !== null
                ? updatedTotal.toFixed(2)
                : total.toFixed(2)}{" "}
              Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PaymentForm;
